import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Plus, Check, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import {
  fetchAllTags,
  createTag,
  type TicketTag,
} from '@/lib/api/support-tickets';

interface TicketTagInputProps {
  selectedTags: TicketTag[];
  onTagsChange: (tags: TicketTag[]) => void;
}

export function TicketTagInput({
  selectedTags,
  onTagsChange,
}: TicketTagInputProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();

  const { data: allTags = [] } = useQuery({
    queryKey: ['support-ticket-tags'],
    queryFn: () => fetchAllTags(),
  });

  const createTagMutation = useMutation({
    mutationFn: createTag,
    onSuccess: (newTag) => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket-tags'] });
      onTagsChange([...selectedTags, newTag]);
      setSearch('');
    },
  });

  const toggleTag = (tag: TicketTag) => {
    const isSelected = selectedTags.some((t) => t.id === tag.id);
    if (isSelected) {
      onTagsChange(selectedTags.filter((t) => t.id !== tag.id));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const removeTag = (tagId: number) => {
    onTagsChange(selectedTags.filter((t) => t.id !== tagId));
  };

  const handleCreateTag = () => {
    const trimmed = search.trim();
    if (!trimmed) return;
    createTagMutation.mutate({ name: trimmed });
  };

  const filteredTags = allTags.filter((tag) =>
    tag.name.toLowerCase().includes(search.toLowerCase())
  );

  const exactMatch = allTags.some(
    (tag) => tag.name.toLowerCase() === search.trim().toLowerCase()
  );

  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {selectedTags.map((tag) => (
        <Badge
          key={tag.id}
          className="text-xs text-white cursor-default gap-1 pr-1"
          style={{ backgroundColor: tag.color || '#6b7280' }}
        >
          {tag.name}
          <button
            type="button"
            onClick={() => removeTag(tag.id)}
            className="ml-0.5 rounded-full hover:bg-white/20 p-0.5"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs gap-1"
          >
            <Plus className="h-3 w-3" />
            <Tag className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or create tag..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {search.trim() ? (
                  <button
                    type="button"
                    onClick={handleCreateTag}
                    className="w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded-sm cursor-pointer"
                  >
                    Create &ldquo;{search.trim()}&rdquo;
                  </button>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    Type to search or create
                  </span>
                )}
              </CommandEmpty>
              {filteredTags.length > 0 && (
                <CommandGroup>
                  {filteredTags.map((tag) => {
                    const isSelected = selectedTags.some(
                      (t) => t.id === tag.id
                    );
                    return (
                      <CommandItem
                        key={tag.id}
                        value={tag.name}
                        onSelect={() => toggleTag(tag)}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-2 w-full">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: tag.color || '#6b7280',
                            }}
                          />
                          <span className="flex-1">{tag.name}</span>
                          {isSelected && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
              {search.trim() && !exactMatch && filteredTags.length > 0 && (
                <CommandGroup>
                  <CommandItem
                    onSelect={handleCreateTag}
                    className="cursor-pointer"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create &ldquo;{search.trim()}&rdquo;
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
